'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type Politeness = 'polite' | 'assertive';

export interface AnnounceOptions {
  politeness?: Politeness;
  repeat?: boolean;
  delay?: boolean;
  intervalMs?: number;
}

interface A11yAnnouncementContextValue {
  announce: (message: string, options?: AnnounceOptions) => void;
  stopRepeat: (politeness: Politeness) => void;
}

const A11yAnnouncementContext = createContext<A11yAnnouncementContextValue | null>(null);

const DEFAULT_INTERVAL_MS = 5000;

export function A11yAnnouncementProvider({
  children,
  intervalMs: defaultIntervalMs = DEFAULT_INTERVAL_MS,
}: {
  children: React.ReactNode;
  intervalMs?: number;
}) {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');

  // Timer refs
  const assertiveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const assertiveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const politeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const politeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Empties a region after it has shown a message. Kept apart from the timers above, which `announce` and
  // `stopRepeat` cancel: a cancelled empty left the text in place, so the same message next time was no
  // change and was not read.
  const assertiveEmptyRef = useRef<NodeJS.Timeout | null>(null);
  const politeEmptyRef = useRef<NodeJS.Timeout | null>(null);
  // What each region holds right now, which the timer callbacks cannot read from state.
  const assertiveLiveRef = useRef('');
  const politeLiveRef = useRef('');

  const publish = useCallback((politeness: Politeness, text: string) => {
    const setMessage = politeness === 'polite' ? setPoliteMessage : setAssertiveMessage;
    const liveRef = politeness === 'polite' ? politeLiveRef : assertiveLiveRef;
    liveRef.current = text;
    setMessage(text);
  }, []);

  const republish = useCallback(
    (politeness: Politeness, text: string) => {
      const timeoutRef = politeness === 'polite' ? politeTimeoutRef : assertiveTimeoutRef;
      const emptyRef = politeness === 'polite' ? politeEmptyRef : assertiveEmptyRef;
      const liveRef = politeness === 'polite' ? politeLiveRef : assertiveLiveRef;
      const show = () => {
        if (emptyRef.current) clearTimeout(emptyRef.current);
        publish(politeness, text);
        emptyRef.current = setTimeout(() => publish(politeness, ''), defaultIntervalMs / 2);
      };

      if (liveRef.current !== text) {
        show();
        return;
      }
      // A live region reads changes, so the same text set again is silent until the region empties first.
      publish(politeness, '');
      timeoutRef.current = setTimeout(show, 0);
    },
    [defaultIntervalMs, publish],
  );

  const clearIntervalFor = useCallback((politeness: Politeness) => {
    const ref = politeness === 'polite' ? politeIntervalRef : assertiveIntervalRef;
    if (ref.current) {
      clearInterval(ref.current);
      ref.current = null;
    }
  }, []);

  const clearTimeoutFor = useCallback((politeness: Politeness) => {
    const ref = politeness === 'polite' ? politeTimeoutRef : assertiveTimeoutRef;
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  }, []);

  const startIntervalFor = useCallback(
    (politeness: Politeness, text: string, intervalMs: number) => {
      const interval = setInterval(() => {
        republish(politeness, text);
      }, intervalMs);
      if (politeness === 'polite') politeIntervalRef.current = interval;
      else assertiveIntervalRef.current = interval;
    },
    [republish],
  );

  const announce = useCallback(
    (message: string, options?: AnnounceOptions) => {
      const politeness: Politeness = options?.politeness ?? 'polite';
      const intervalMs = options?.intervalMs ?? defaultIntervalMs;

      clearIntervalFor(politeness);
      clearTimeoutFor(politeness);

      if (options?.repeat && options?.delay) {
        // Delay the first publish if requested, then start the interval after it fires
        const timeout = setTimeout(() => {
          republish(politeness, message);
          startIntervalFor(politeness, message, intervalMs);
        }, defaultIntervalMs);
        if (politeness === 'polite') politeTimeoutRef.current = timeout;
        else assertiveTimeoutRef.current = timeout;
      } else {
        republish(politeness, message);
        if (options?.repeat) startIntervalFor(politeness, message, intervalMs);
      }
    },
    [clearIntervalFor, clearTimeoutFor, republish, startIntervalFor, defaultIntervalMs],
  );

  const stopRepeat = useCallback(
    (politeness: Politeness) => {
      clearIntervalFor(politeness);
      clearTimeoutFor(politeness);
    },
    [clearIntervalFor, clearTimeoutFor],
  );

  useEffect(() => {
    return () => {
      if (assertiveIntervalRef.current) clearInterval(assertiveIntervalRef.current);
      if (assertiveTimeoutRef.current) clearTimeout(assertiveTimeoutRef.current);
      if (politeIntervalRef.current) clearInterval(politeIntervalRef.current);
      if (politeTimeoutRef.current) clearTimeout(politeTimeoutRef.current);
      if (assertiveEmptyRef.current) clearTimeout(assertiveEmptyRef.current);
      if (politeEmptyRef.current) clearTimeout(politeEmptyRef.current);
    };
  }, []);

  const value = useMemo(() => ({ announce, stopRepeat }), [announce, stopRepeat]);

  return (
    <A11yAnnouncementContext.Provider value={value}>
      {children}
      <output aria-live="assertive" aria-atomic="true" className="sr-only">
        {assertiveMessage}
      </output>
      <output aria-live="polite" aria-atomic="true" className="sr-only">
        {politeMessage}
      </output>
    </A11yAnnouncementContext.Provider>
  );
}

export const useA11yAnnouncement = () => {
  const ctx = useContext(A11yAnnouncementContext);
  if (!ctx) throw new Error('useA11yAnnouncement must be used within an <A11yAnnouncementProvider>');
  return ctx;
};
